import { fabric } from 'fabric';
import 'fabric-customise-controls';
import FontFaceObserver from 'fontfaceobserver';
import * as util from '../../shared/Utilities';
import { Translate } from '../../shared/LanguageService';
import * as AnnotationTypes from './AnnotationTypes';
import { IBooleanWrapper, IObjectWrapper } from './Types';
import * as Strings from './Strings';
import createPaintBrush from './PaintBrush';
import createSuperTextbox from './SuperTextbox';

let _strokeWidth = 5;
let _strokeColor: any = {};
let _drawTextsAsUpperCase: boolean = false;
let _fontFamily: string = null;
let _fontSizeRatio: number = 1;
let isFirstRenderDone = false;

let defaultCanvasState = '';
let canvasStates = [];
let canAddToStateArray = true;
let isDragging = false;

const controlBorderSettings = {
    borderColor: '#ABE336',
    cornerColor: '#ABE336',
    borderScaleFactor: 2,
    cornerSize: 10,
    cornerStyle: 'circle',
    transparentCorners: false
};

const controlBorderTransparentSettings = {
    borderColor: '#ABE336',
    cornerColor: '#ABE336',
    borderScaleFactor: 2,
    cornerSize: 10,
    cornerStyle: 'circle',
    transparentCorners: true
};

const scaleTransparentLimit = 0.4;

function setControlBorderSettings(activeObj: any) {
    if (activeObj && activeObj.ownMatrixCache) {
        let scale = Math.min(activeObj.ownMatrixCache.value[0], activeObj.ownMatrixCache.value[3]);
        if (scale < scaleTransparentLimit) {
            activeObj.set(controlBorderTransparentSettings);
        } else {
            activeObj.set(controlBorderSettings);
        }
    } else if (activeObj) {
        activeObj.set(controlBorderSettings);
    }
}

let customPropertiesWhitelistForCloning = [
    'isComment',
    'storey',
    'customStorey',
    'lockScalingX',
    'lockScalingY',
    '__skipDimension'
];
const whitelistProperties = [
    'selectable',
    'id',
    'angle',
    'flipX',
    'flipY',
    'fill',
    'backgroundColor',
    'stroke',
    'strokeDashArray',
    'visible',
    'strokeWidth',
    'scaleX',
    'originX',
    'originY',
    'left',
    'top',
    'storey',
    'customStorey',
    'name',
    'isComment',
    'corner'
];

export function constructFabric(): any {
    return fabric;
}

export function constructFilePlacementsCanvas(
    fabric: any,
    canvasId: string,
    canvasContainerWidth: number,
    canvasContainerHeight: number,
    images: any[]
): any {
    let canvas = new fabric.Canvas(canvasId);
    canvas.backgroundColor = 'gainsboro';

    addCanvasObjectToElement(canvas, canvasId);

    const canvasViewportWidth = canvasContainerWidth;

    canvas.setWidth(canvasViewportWidth);
    canvas.setHeight(canvasViewportWidth);

    canvas.renderAll();
    canvas.calcOffset();

    let addedImagesDone = false;
    let desiredHeight = canvas.height / images.length;
    let nextHeight = 0;
    let heightOffset = 20;
    let index = 0;
    let wasPreviouslyScaledAndPlaced = false;
    images.forEach((x) => {
        fabric.Image.fromURL(x.url, (planImage: any) => {
            let desiredScale = Math.min(desiredHeight / (planImage.height + heightOffset), 1);

            if (x.placement && x.placement.rotation) {
                planImage.rotate(x.placement.rotation);

                switch (planImage.angle) {
                    case 90:
                        planImage.originX = 'left';
                        planImage.originY = 'bottom';
                        break;
                    case 180:
                        planImage.originX = 'right';
                        planImage.originY = 'bottom';
                        break;
                    case -90:
                    case 270:
                        planImage.originX = 'right';
                        planImage.originY = 'top';
                        break;
                    default:
                        planImage.originX = 'left';
                        planImage.originY = 'top';
                        break;
                }
            }

            if (x.placement && x.placement.height) {
                wasPreviouslyScaledAndPlaced = true;

                let width = x.placement.width * x.placement.groupWidth;
                let height = x.placement.height * x.placement.groupHeight;

                let xScale = canvasContainerWidth / x.placement.groupWidth;
                let yScale = canvasContainerHeight / x.placement.groupHeight;
                let scale = Math.min(xScale, yScale);

                planImage.set({
                    id: x.id,
                    selectable: true,
                    hasRotatingPoint: false,
                    lockScalingFlip: true,
                    lockRotation: true,
                    lockUniScaling: true,
                    scaleX: (width / planImage.width) * scale,
                    scaleY: (height / planImage.height) * scale,
                    left: x.placement.left * (x.placement.groupWidth * scale),
                    top: x.placement.top * (x.placement.groupHeight * scale)
                });
            } else {
                planImage.set({
                    id: x.id,
                    selectable: true,
                    hasRotatingPoint: false,
                    lockScalingFlip: true,
                    lockRotation: true,
                    lockUniScaling: true,
                    scaleX: desiredScale,
                    scaleY: desiredScale,
                    left: Math.max(canvasViewportWidth / 2 - planImage.width * desiredScale, 0),
                    top: nextHeight
                });
            }

            nextHeight += planImage.height * desiredScale + heightOffset;
            canvas.add(planImage);

            if (index === images.length - 1) {
                addedImagesDone = true;
                canvas.renderAll();
            }

            index++;
        });
    });

    const opacity = 0.8;

    let firstRenderDone = false;
    canvas.on('after:render', (e) => {
        if (firstRenderDone || !addedImagesDone) {
            return;
        }

        firstRenderDone = true;
        const offset = 50;
        let group = selectAll(fabric, canvas);
        group.canvas = canvas;
        let boundingRect = group.getBoundingRect();

        group.originX = 'center';
        group.originY = 'center';
        if (wasPreviouslyScaledAndPlaced) {
            group.left = canvasContainerWidth / 2;
            group.top = canvasContainerHeight / 2;
        } else {
            let cWidth = (canvas.width - offset) / boundingRect.width;
            let cHeight = (canvas.height - offset) / boundingRect.height;
            let cScale = Math.min(cWidth, cHeight);
            group.scaleX = cScale;
            group.scaleY = cScale;

            group.left = canvas.width / 2;
            group.top = canvas.height / 2;
        }

        canvas.discardActiveObject();
        canvas.renderAll();
    });

    canvas.on('object:selected', (event: any, e: any) => {
        var activeObj = event.target;
        if (activeObj.get('type') == 'group') {
            activeObj.set({ borderColor: '#fbb802', cornerColor: '#fbb802' });
        }

        canvas.bringToFront(event.target);
        event.target.opacity = opacity;

        if (event.target && event.target.ownMatrixCache) {
            let scale = Math.min(
                event.target.ownMatrixCache.value[0],
                event.target.ownMatrixCache.value[3]
            );
            if (scale < scaleTransparentLimit) {
                event.target.set(controlBorderTransparentSettings);
            } else {
                event.target.set(controlBorderSettings);
            }
        } else {
            event.target.set(controlBorderSettings);
        }

        let objects = canvas.getObjects();

        for (let i = 0; i < objects.length; i++) {
            const item = objects[i];
            if (item !== event.target) {
                item.opacity = 1.0;
            }
        }
    });

    canvas.on('selection:updated', (event: any, e: any) => {
        canvas.bringToFront(event.target);

        if (event.target) {
            event.target.opacity = opacity;
            if (event.target && event.target.ownMatrixCache) {
                let scale = Math.min(
                    event.target.ownMatrixCache.value[0],
                    event.target.ownMatrixCache.value[3]
                );
                if (scale < scaleTransparentLimit) {
                    event.target.set(controlBorderTransparentSettings);
                } else {
                    event.target.set(controlBorderSettings);
                }
            } else {
                event.target.set(controlBorderSettings);
            }
        }

        let objects = canvas.getObjects();

        for (let i = 0; i < objects.length; i++) {
            const item = objects[i];

            if (!event.target || item !== event.target) {
                item.opacity = 1.0;
            }
        }
    });

    canvas.on('selection:cleared', (e: any) => {
        let objects = canvas.getObjects();

        for (let i = 0; i < objects.length; i++) {
            objects[i].opacity = 1.0;
        }
    });

    return canvas;
}

export function constructCanvasWithImage(
    fabric: any,
    mouseIsDown: any,
    isCropping: IBooleanWrapper,
    isDrawing: IBooleanWrapper,
    isDrawingEllipse: IBooleanWrapper,
    isDrawingRectangle: IBooleanWrapper,
    selectedStorey: IObjectWrapper,
    canvasId: string,
    url: string,
    deselectStoreyCallback: () => void,
    state: string,
    canvasFontFamily: string,
    canvasFontSize: number,
    fontSizeRatio: number,
    drawTextsAsUpperCase: boolean,
    selectedObjectCallback: () => void,
    deselectedObjectCallback: () => void,
    strokeWidth: number,
    strokeColor: any,
    isColorPipette: IBooleanWrapper,
    colorChangeCallback: (rgb: any) => void,
    deactivateColorPipette: () => void,
    canvasHasChanged: (isSymbol?: boolean) => void,
    annotationType: string,
    setEditingTextState: (isEditingText: boolean) => void,
    textsOnly: boolean,
    allowedToPan: () => boolean,
    afterFirstRenderCallback?: () => boolean
): any {
    _strokeWidth = strokeWidth;
    _strokeColor = strokeColor;
    _fontSizeRatio = fontSizeRatio;

    _drawTextsAsUpperCase = drawTextsAsUpperCase;

    if (canvasFontFamily) {
        new FontFaceObserver(canvasFontFamily).load().then(() => {
            _fontFamily = canvasFontFamily;
        });
    }

    let canvas = new fabric.Canvas(canvasId);
    canvas.freeDrawingBrush.color = `rgba(${_strokeColor.r}, ${_strokeColor.g}, ${_strokeColor.b}, ${_strokeColor.a})`;
    canvas.freeDrawingBrush.width = _strokeWidth;
    canvas.freeDrawingCursor = 'default';
    canvas.fontFamily = canvasFontFamily;
    canvas.fontSize = (canvasFontSize ? canvasFontSize : 48) * _fontSizeRatio;
    canvas.setBackgroundColor('rgba(255, 255, 255, 1)');
    canvas.hoverCursor = 'default';
    canvas.moveCursor = 'default';
    canvas.defaultCursor = 'default';
    canvas.selectionColor = '#ABE336';
    canvas.selectionBorderColor = '#ABE336';
    canvas.selectionLineWidth = 10;
    canvasStates = [];
    canAddToStateArray = true;
    createPaintBrush(fabric);
    createSuperTextbox(fabric);
    canvas.on('after:render', (e) => {
        if (!isFirstRenderDone) {
            isFirstRenderDone = true;
            if (typeof afterFirstRenderCallback === 'function') {
                let success = afterFirstRenderCallback();

                if (!success) {
                    isFirstRenderDone = false;
                } else {
                    createSuperTextboxes(canvas, fabric);
                    canvas.renderAll();
                    defaultCanvasState = canvas.toJSON(whitelistProperties);
                }
            }

            if (isFirstRenderDone && state) {
                let objects = canvas.getObjects();
                for (let i = 0; i < objects.length; i++) {
                    let item = objects[i];
                    if (item.type === 'textbox' && !item.isComment) {
                        item.setControlsVisibility({
                            mt: false, // middle top
                            mb: false, // midle bottom
                            ml: false, // middle left
                            mr: false, // middle right
                            bl: false, // bottom left
                            br: false, // bottom right
                            tl: false, // top left
                            tr: false, // top right
                            mtr: false // middle-top-rotate control
                        });

                        addMovementAxisLimitation(item, textsOnly, canvas);
                        addRemoveEmptyTextBoxesEvent(item, textsOnly, canvas, setEditingTextState);
                    }
                }
            }
        }
    });

    canvas.on('object:modified', () => {
        if (isFirstRenderDone) {
            canvasHasChanged();
        }

        canvas
            .getObjects()
            .filter((x) => x.type === 'textbox')
            .forEach((x) => {
                canvas.bringToFront(x);
            });

        canvasStates.push(canvas.toJSON(whitelistProperties));
    });

    canvas.on('object:removed', () => {
        if (isFirstRenderDone) {
            canvasHasChanged();
        }
        if (!isDragging && canAddToStateArray) {
            canvasStates.push(canvas.toJSON(whitelistProperties));
        }
    });

    canvas.on('object:added', (event: any, e: any) => {
        if (isFirstRenderDone) {
            let isSymbol = false;

            if (event.target && event.target._element) {
                isSymbol = event.target._element.className.includes('annotation-symbol');
            }

            canvasHasChanged(isSymbol);

            canvas
                .getObjects()
                .filter((x) => x.type === 'textbox')
                .forEach((x) => {
                    canvas.bringToFront(x);
                });

            if (event.target.type === 'textbox') {
                addMovementAxisLimitation(event.target, textsOnly, canvas);
                event.target.hoverCursor = 'default';
                event.target.moveCursor = 'default';
                event.target.defaultCursor = 'default';
            }
        }

        if (event.target && event.target.ownMatrixCache) {
            let scale = Math.min(
                event.target.ownMatrixCache.value[0],
                event.target.ownMatrixCache.value[3]
            );
            if (scale < scaleTransparentLimit) {
                event.target.set(controlBorderTransparentSettings);
            } else {
                event.target.set(controlBorderSettings);
            }
        } else {
            event.target.set(controlBorderSettings);
        }

        if (
            canAddToStateArray &&
            !isDragging &&
            !isDrawing &&
            !isDrawingEllipse &&
            !isDrawingRectangle
        ) {
            canvasStates.push(canvas.toJSON(whitelistProperties));
        }
    });

    function deselectStorey() {
        if (selectedStorey.value) {
            selectedStorey.value.stroke = Strings.storeyMarkerColor;

            selectedStorey.value = null;
            deselectStoreyCallback();
        }
    }

    canvas.on('selection:created', (e: any) => {
        let activeObj = e.target;

        let currentSetting;
        if (activeObj && activeObj.ownMatrixCache) {
            let scale = Math.min(
                activeObj.ownMatrixCache.value[0],
                activeObj.ownMatrixCache.value[3]
            );
            if (scale < scaleTransparentLimit) {
                currentSetting = controlBorderTransparentSettings;
                activeObj.set(controlBorderTransparentSettings);
            } else {
                currentSetting = controlBorderSettings;
                activeObj.set(controlBorderSettings);
            }
        } else {
            currentSetting = controlBorderSettings;
            activeObj.set(controlBorderSettings);
        }

        selectedObjectCallback();
        if (e.target) {
            e.target.set(currentSetting);
        }

        if (!e.target.hasOwnProperty('storey')) {
            deselectStorey();
        }

        if (e.target.id === 'cropRect') {
            const mouse = canvas.getPointer(e);
            const active = canvas.getActiveObject();

            let targets = canvas
                .getObjects()
                .filter((x, i) => i !== 0 && x.containsPoint(mouse) && x.id !== 'cropRect');
            if (targets.length > 0 && targets[0] !== active) {
                canvas.setActiveObject(targets[0]);
            }
        }
    });

    canvas.on('selection:updated', (event: any, e: any) => {
        setControlBorderSettings(event.target);
    });

    canvas.on('text:editing:entered', () => {
        setEditingTextState(true);
    });

    canvas.on('text:editing:exited', () => {
        setEditingTextState(false);
        let emptyTextBoxes = canvas.getObjects().filter((x) => x.type === 'textbox' && !x.text);
        for (let i = 0; i < emptyTextBoxes.length; i++) {
            canvas.remove(emptyTextBoxes[i]);
        }
    });

    let isMouseMoving = false;
    canvas.on('mouse:down', (e: any) => {
        isMouseMoving = false;
    });

    canvas.on('mouse:move', () => {
        isMouseMoving = true;
    });

    canvas.on('mouse:up', (e: any) => {
        if (!isMouseMoving && e.target && e.target.id === 'cropRect') {
            const mouse = canvas.getPointer(e.e, true);
            const active = canvas.getActiveObject();

            let targets = canvas
                .getObjects()
                .filter((x, i) => i !== 0 && x.containsPoint(mouse) && x.id !== 'cropRect');
            if (targets.length > 0 && targets[0] !== active) {
                canvas.setActiveObject(targets[0]);
            }
        }

        isMouseMoving = false;
    });

    canvas.on('mouse:wheel', function (event: any) {
        let e = event.e;
        if (e.ctrlKey === true) {
            let delta = event.e.deltaY * -1;
            let zoom = canvas.getZoom();
            zoom = zoom + delta / 2000;
            if (zoom > 20) zoom = 20;
            if (zoom < 0.1) zoom = 0.1;
            canvas.zoomToPoint({ x: e.offsetX, y: e.offsetY }, zoom);
            event.e.preventDefault();
            event.e.stopPropagation();
        }
    });

    canvas.on('mouse:down', function (event: any) {
        let e = event.e;
        if (e.ctrlKey === true && allowedToPan()) {
            this.isDragging = true;
            this.selection = false;
            this.lastPosX = event.e.clientX;
            this.lastPosY = event.e.clientY;
        }
    });

    canvas.on('mouse:move', function (event: any) {
        if (this.isDragging && allowedToPan()) {
            let e = event.e;
            this.viewportTransform[4] += e.clientX - this.lastPosX;
            this.viewportTransform[5] += e.clientY - this.lastPosY;
            this.requestRenderAll();
            this.lastPosX = e.clientX;
            this.lastPosY = e.clientY;
        }
    });

    canvas.on('mouse:up', function (event: any) {
        this.isDragging = false;
        this.selection = true;
        canvas.forEachObject((o) => {
            o.setCoords();
            setControlBorderSettings(o);
        });
    });

    canvas.on('before:selection:cleared', (e: any) => {
        deselectedObjectCallback();
        deselectStorey();
    });

    configureRectangleEvents(
        fabric,
        canvas,
        mouseIsDown,
        isCropping,
        isDrawing,
        isDrawingEllipse,
        isDrawingRectangle,
        isColorPipette,
        deactivateColorPipette,
        colorChangeCallback,
        deselectStoreyCallback
    );

    addCanvasObjectToElement(canvas, canvasId);
    addObjectsToCanvas(fabric, canvas, state, url, annotationType, false, canvas.fontSize);
    return canvas;
}

function addCanvasObjectToElement(canvas: any, canvasId: string) {
    let element = document.getElementById(canvasId);
    element[Strings.canvasObjectPropertyName] = canvas;
}

let rev = false;
function addObjectsToCanvas(
    fabric: any,
    canvas: any,
    state: string,
    url: string,
    annotationType: string,
    selectable: boolean = false,
    fontSize: number | null = null
): any {
    canvas.selection = true;

    if (state && state !== '{}') {
        let o = JSON.parse(state);
        o.objects[0].src = url;
        o.objects[0].selectable = false;

        for (let i = 0; i < o.objects.length; i++) {
            if (i !== 0) {
                if (o.objects[i].src) {
                    o.objects[i].src = makeAbsoluteUrlRelative(o.objects[i].src);
                }

                if (
                    (annotationType === AnnotationTypes.AnnotationNormal ||
                        annotationType === AnnotationTypes.ExternalAnnotation ||
                        annotationType === AnnotationTypes.DeliveryAnnotation ||
                        annotationType === AnnotationTypes.ExternalDeliveryAnnotation) &&
                    o.objects[i].type === 'textbox' &&
                    !o.objects[i].isComment
                ) {
                    o.objects[i].selectable = false;
                    o.objects[i].editable = false;
                } else {
                    o.objects[i].selectable = true;

                    if (fontSize && o.objects[i].type === 'textbox') {
                        o.objects[i].fontSize = fontSize;
                    }
                }
            }
        }
        canAddToStateArray = false;
        canvas.loadFromJSON(JSON.stringify(o), () => {
            canvas.renderAll.bind(canvas);
            canAddToStateArray = true;
        });
        handlePotentialCropping(canvas);
        rev = true;
    } else {
        createFromImage(fabric, canvas, url, selectable);
    }
    isFirstRenderDone = false;
}

export function createFromImage(
    fabric: any,
    canvas: any,
    url: string,
    selectable: boolean = false,
    callback: any = null
) {
    fabric.Image.fromURL(url, (planImage: any) => {
        planImage.set({
            height: planImage.height,
            width: planImage.width,
            selectable: selectable,
            left: 0,
            top: 0,
            originX: 'left',
            originY: 'top',
            padding: 0,
            strokeWidth: 0,
            backgroundColor: 'rgb(255,255,255)'
        });
        canvas.add(planImage);
        if (callback) {
            callback(planImage);
        }
    });
}

export function addTextBoxes(fabric: any, canvas: any, textboxes: any) {
    fabric.util.enlivenObjects(textboxes, (objects) => {
        objects.forEach((o) => {
            canvas.add(o);
        });

        canvas.renderAll();
    });
}

function handlePotentialCropping(canvas: any) {
    let cropRects = document.getElementsByClassName(Strings.cropRectangleName);
    if (cropRects && cropRects.length === 1) {
        zoomAndPanAfterCrop(canvas._objects[0], cropRects[0]);
    }
}

function configureRectangleEvents(
    fabric: any,
    canvas: any,
    mouseIsDown: any,
    isCropping: IBooleanWrapper,
    isDrawing: IBooleanWrapper,
    isDrawingEllipse: IBooleanWrapper,
    isDrawingRectangle: IBooleanWrapper,
    isColorPipette: IBooleanWrapper,
    deactivateColorPipette: () => void,
    colorChangeCallback: (rgb: any) => void,
    deselectStoreyCallback: () => void
): void {
    let rectModifying = false;
    let element, origX, origY;

    let context = canvas.getContext('2d');

    canvas.on('mouse:down', (event: any) => {
        if (isColorPipette.value === true) {
            return;
        }

        if (!canvas.getActiveObject()) {
            deselectStoreyCallback();
        }

        if (isDrawingEllipse.value || isDrawingRectangle.value || isCropping.value) {
            canvas.selection = false;
            let pointer = canvas.getPointer(event.e);
            origX = pointer.x;
            origY = pointer.y;

            let obj: any = {
                left: origX,
                top: origY,
                originX: 'left',
                originY: 'top',
                fill: 0,
                opacity: _strokeColor.a,
                stroke: `rgba(${_strokeColor.r}, ${_strokeColor.g}, ${_strokeColor.b}, ${_strokeColor.a})`,
                strokeWidth: _strokeWidth
            };

            if (isDrawingEllipse.value) {
                obj.rx = pointer.x - origX;
                obj.ry = pointer.y - origY;
                element = new fabric.Ellipse(obj);
            }

            if (isDrawingRectangle.value) {
                obj.width = pointer.x - origX;
                obj.height = pointer.y - origY;
                element = new fabric.Rect(obj);
            }

            if (isCropping.value) {
                obj.stroke = Strings.cropRectangleColor;
                obj.width = pointer.x - origX;
                obj.height = pointer.y - origY;
                element = new fabric.Rect(obj);
            }

            if (element) {
                canvas.add(element);
            }
        }

        if (isDrawing.value) {
            element = new fabric.PaintBrush(canvas);
            element.color = `rgba(${_strokeColor.r}, ${_strokeColor.g}, ${_strokeColor.b}, ${_strokeColor.a})`;
            element.width = _strokeWidth;

            canvas.freeDrawingBrush = element;

            // Adding two initial points becuase fabric.js expecting at least two points in the method convertPointsToSVGPath.
            element.onMouseMove(canvas.getPointer(event.e), event.e.shiftKey, event.e.ctrlKey);
            element.onMouseMove(canvas.getPointer(event.e), event.e.shiftKey, event.e.ctrlKey);
        }

        mouseIsDown = event.e;
    });

    canvas.on('mouse:move', (event: any) => {
        setControlBorderSettings(event.target);

        if (isColorPipette.value === true) {
            let mouse = canvas.getPointer(event.e, true);
            let px = context.getImageData(mouse.x, mouse.y, 1, 1).data;

            let rgb = {
                r: px[0],
                g: px[1],
                b: px[2],
                a: 1
            };

            colorChangeCallback(rgb);
            return;
        }

        if (rectModifying) {
            return;
        }

        if (!mouseIsDown) {
            return;
        }

        if (isDrawing.value) {
            element.onMouseMove(canvas.getPointer(event.e), event.e.shiftKey, event.e.ctrlKey);
        }

        if (isDrawingEllipse.value) {
            drawEllipse(element, canvas, event, origX, origY);
            canvas.renderAll();
        }

        if (isDrawingRectangle.value) {
            drawRect(element, canvas, event, origX, origY);
            canvas.renderAll();
        }

        if (isCropping.value) {
            drawRect(element, canvas, event, origX, origY, 'cropRect');
            canvas.renderAll();
        }
    });

    canvas.on('mouse:up', () => {
        if (isColorPipette.value === true) {
            deactivateColorPipette();
            return;
        }

        if (rectModifying) {
            rectModifying = false;
            return;
        }

        mouseIsDown = null;
        canvas.selection = true;

        if (isDrawing.value || isDrawingEllipse.value || isDrawingRectangle.value) {
            canvasStates.push(canvas.toJSON(whitelistProperties));
        }
    });

    canvas.on('object:moving', (event: any) => {
        if (!event.target) {
            return;
        }

        if (event.target.type === 'textbox') {
            let offset = 2;
            event.target.originX = 'center';
            event.target.originY = 'center';
            let widthFromCenter = event.target.width / 2;
            let heightFromCenter = event.target.height / 2;

            let canvasWidth = canvas.width / canvas.getZoom();
            if (event.target.left < widthFromCenter) {
                event.target.left = widthFromCenter + offset;
            } else if (event.target.left + widthFromCenter > canvasWidth) {
                event.target.left = canvasWidth - widthFromCenter - offset;
            }

            let canvasHeight = canvas.height / canvas.getZoom();
            if (event.target.top < heightFromCenter) {
                event.target.top = heightFromCenter + offset;
            } else if (event.target.top + heightFromCenter > canvasHeight) {
                event.target.top = canvasHeight - heightFromCenter - offset;
            }
        }
    });
}

function drawEllipse(element: any, canvas: any, event: any, origX: number, origY: number): void {
    let pointer = canvas.getPointer(event.e);
    let rx = Math.abs(origX - pointer.x) / 2;
    let ry = Math.abs(origY - pointer.y) / 2;

    if (event.e.shiftKey) {
        ry = rx;
    }

    if (rx > element.strokeWidth) {
        rx -= element.strokeWidth / 2;
    }
    if (ry > element.strokeWidth) {
        ry -= element.strokeWidth / 2;
    }
    element.set({ rx: rx, ry: ry });

    if (origX > pointer.x) {
        element.set({ originX: 'right' });
    } else {
        element.set({ originX: 'left' });
    }
    if (origY > pointer.y) {
        element.set({ originY: 'bottom' });
    } else {
        element.set({ originY: 'top' });
    }
    element.set({ selectable: false });
    element.setCoords();

    setControlBorderSettings(element);
}

function drawRect(
    element: any,
    canvas: any,
    event: any,
    origX: number,
    origY: number,
    id?: string
): void {
    let pointer = canvas.getPointer(event.e);

    let width = Math.abs(origX - pointer.x);
    let height = Math.abs(origY - pointer.y);

    if (event.e.shiftKey) {
        height = width;
    }

    if (origX > pointer.x) {
        element.set({ originX: 'right' });
    } else {
        element.set({ originX: 'left' });
    }
    if (origY > pointer.y) {
        element.set({ originY: 'bottom' });
    } else {
        element.set({ originY: 'top' });
    }

    if (id) {
        element.set({ id: id });
    }

    element.set({ width: width });
    element.set({ height: height });
    element.set({ selectable: false });
    element.setCoords();
    setControlBorderSettings(element);
}

function setGroupPosition(fabric: any, canvas: any, left: number, top: number) {
    let group = selectAll(fabric, canvas);
    group.originX = 'left';
    group.originY = 'top';
    group.left = left;
    group.top = top;
    group.setCoords();
    setControlBorderSettings(group);
    canvas.discardActiveObject();
    canvas.renderAll();
    canvas.calcOffset();
}

let mouseOffsetX = 0;
let mouseOffsetY = 0;

let iconWidth = 0;
let iconHeight = 0;
let currentText;

export function configureDraggingEvents(
    fabric: any,
    canvas: any,
    canvasContainer: any,
    dropCallback: () => void,
    dropCommentCallback: () => void,
    onlyTexts: boolean,
    exitTextEditingCallback: (isEditingText: boolean) => void
): void {
    if (onlyTexts) {
        canvas.on('text:changed', (event) => {
            let item = event.target;
            item.text = _drawTextsAsUpperCase ? item.text.toUpperCase() : item.text;

            item.set({
                width: getTextSize(item.text, canvas).width
            });
        });

        canvas.on('selection:cleared', (event, deselected) => {
            if (event.deselected) {
                for (let i = 0; i < event.deselected.length; i++) {
                    const item = event.deselected[i];

                    if (item.type === 'textbox' && !item.isComment) {
                        trimAndResizeText(canvas, item);
                    }
                }
            }
        });
    }

    let forEachNode = (array: any, callback: (x: any, y: any) => void) => {
        for (let i = 0; i < array.length; i++) {
            callback.call(i, array[i]);
        }
    };

    let dragImage = new Image();
    // 1px transparent image
    dragImage.src =
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

    const d: any = document;
    const w: any = window;

    // Internet Explorer 6-11
    const isIE = false || !!d.documentMode;

    // Edge 20+
    const isEdge = !isIE && !!w.StyleMedia;

    let images = document.querySelectorAll('img.annotation-symbol:not(.hasdragevents)');
    forEachNode(images, (img: any) => {
        let touchState = '';
        let touch;

        img.addEventListener(
            'touchstart',
            (event) => {
                let dragStartEvent = new MouseEvent('dragstart');
                img.dispatchEvent(dragStartEvent);
            },
            false
        );
        img.addEventListener(
            'touchmove',
            util.throttle((event) => {
                touch = event.touches.item(0);

                let elements: Element[] = document.elementsFromPoint(touch.clientX, touch.clientY);

                let annotationContainer;

                for (var i = 0; i < elements.length; i++) {
                    const el = elements[i];
                    if (el.id === 'annotation-container') {
                        annotationContainer = el;
                        break;
                    }
                }

                if (annotationContainer) {
                    //FAKE OVER
                    let mouseEventInit: MouseEventInit = {
                        screenX: touch.screenX,
                        screenY: touch.screenY,
                        clientX: touch.clientX,
                        clientY: touch.clientY
                    };

                    var dragover = new MouseEvent('dragover', mouseEventInit);
                    canvasContainer.dispatchEvent(dragover);
                    touchState = 'over';
                } else if (!annotationContainer && touchState === 'over') {
                    //FAKE LEAVE
                    var dragleave = new Event('dragleave');
                    canvasContainer.dispatchEvent(dragleave);
                    touchState = 'leave';
                }
            }, 10),
            false
        );
        img.addEventListener(
            'touchend',
            (event) => {
                let elements: Element[] = document.elementsFromPoint(touch.clientX, touch.clientY);
                for (var i = 0; i < elements.length; i++) {
                    const el = elements[i];
                    if (el.id === 'annotation-container') {
                        var dragdrop = new Event('drop');
                        canvasContainer.dispatchEvent(dragdrop);
                        break;
                    }
                }

                dragEnd(event);
            },
            false
        );

        img.addEventListener('dragstart', dragStart, false);
        img.addEventListener('dragend', dragEnd, false);

        img.classList.add('hasdragevents');

        function dragStart(event: any) {
            isDragging = true;

            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'copy';
                event.dataTransfer.dropEffect = 'default';

                // setDragImage is not supported in IE and Edge
                if (!isIE && !isEdge) {
                    event.dataTransfer.setDragImage(dragImage, 0, 0);
                }
            }

            forEachNode(images, (img) => {
                img.classList.remove('img_dragging');
            });
            event.target.classList.add('img_dragging');

            if (event.trusted) {
                mouseOffsetX = event.offsetX;
                mouseOffsetY = event.offsetY;
            } else {
                mouseOffsetX = event.target.width / 2;
                mouseOffsetY = event.target.height / 2;
            }
            iconWidth = event.target.width;
            iconHeight = event.target.height;
        }

        function dragEnd(event: any) {
            isDragging = false;
            forEachNode(images, (img) => {
                img.classList.remove('img_dragging');
            });
        }
    });

    // Get Text Panel items
    let textPanel = document.getElementsByClassName(Strings.textPanelItemClassName);
    forEachNode(textPanel, (item: any) => {
        item.classList.add('hasdragevents');
        item.addEventListener('dragstart', dragStart, false);
        let touch;
        let touchState;

        item.addEventListener(
            'touchstart',
            (event) => {
                let dragStartEvent = new MouseEvent('dragstart');
                item.dispatchEvent(dragStartEvent);
            },
            false
        );
        item.addEventListener(
            'touchmove',
            util.throttle((event) => {
                touch = event.touches.item(0);

                let elements: Element[] = document.elementsFromPoint(touch.clientX, touch.clientY);

                let annotationContainer;

                for (var i = 0; i < elements.length; i++) {
                    const el = elements[i];
                    if (el.id === 'annotation-container') {
                        annotationContainer = el;
                        break;
                    }
                }

                if (annotationContainer) {
                    //FAKE OVER
                    let mouseEventInit: MouseEventInit = {
                        screenX: touch.screenX,
                        screenY: touch.screenY,
                        clientX: touch.clientX,
                        clientY: touch.clientY
                    };

                    var dragover = new MouseEvent('dragover', mouseEventInit);
                    canvasContainer.dispatchEvent(dragover);
                    touchState = 'over';
                } else if (!annotationContainer && touchState === 'over') {
                    //FAKE LEAVE
                    var dragleave = new Event('dragleave');
                    canvasContainer.dispatchEvent(dragleave);
                    touchState = 'leave';
                }
            }, 10),
            false
        );

        item.addEventListener(
            'touchend',
            (event) => {
                let elements: Element[] = document.elementsFromPoint(touch.clientX, touch.clientY);
                for (var i = 0; i < elements.length; i++) {
                    const el = elements[i];
                    if (el.id === 'annotation-container') {
                        var dragdrop = new Event('drop');
                        canvasContainer.dispatchEvent(dragdrop);
                        break;
                    }
                }
            },
            false
        );

        function dragStart(event: any) {
            currentText = event.currentTarget.innerHTML;

            if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = 'copy';
                event.dataTransfer.dropEffect = 'default';

                // setDragImage is not supported in IE and Edge
                if (!isIE && !isEdge) {
                    event.dataTransfer.setDragImage(dragImage, 0, 0);
                }

                event.dataTransfer.setData('text/plain', currentText);
            }

            const textSize = getTextSize(event.currentTarget.innerHTML, canvas);
            iconWidth = textSize.width;
            iconHeight = textSize.height;

            if (event.trusted) {
                mouseOffsetX = calcMouseOffset(
                    iconWidth,
                    event.currentTarget.offsetWidth,
                    event.offsetX
                );
                mouseOffsetY = calcMouseOffset(
                    iconHeight,
                    event.currentTarget.offsetHeight,
                    event.offsetY
                );
            } else {
                mouseOffsetX = iconWidth / 2;
                mouseOffsetY = iconHeight / 2;
            }

            return false;
        }
    });

    let hasDragEvents = false;

    for (let i = 0; i < canvasContainer.classList.length; i++) {
        let element = canvasContainer.classList[i];
        hasDragEvents = hasDragEvents ? hasDragEvents : element === 'hasdragevents';
    }

    if (hasDragEvents) {
        return;
    }

    canvasContainer.addEventListener('dragover', util.throttle(dragOver, 10), false);
    canvasContainer.addEventListener('dragleave', dragLeave, false);
    canvasContainer.addEventListener('drop', drop, false);
    canvasContainer.classList.add('hasdragevents');
    let currentObject;
    let loadingImage = false;

    function dragOver(event: any) {
        if (event.preventDefault) {
            event.preventDefault();
        }

        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'default';
        }

        let pointer = canvas.getPointer(event);
        if (!currentObject) {
            // Text content
            if (currentText) {
                let text = _drawTextsAsUpperCase ? currentText.toUpperCase() : currentText;

                let offset = 2;
                let iconWidthFromCenter = iconWidth / 2;
                let iconHeightFromCenter = iconHeight / 2;

                let newText = new fabric.Textbox(text, {
                    left: pointer.x + iconWidthFromCenter - mouseOffsetX,
                    top: pointer.y + iconHeightFromCenter - mouseOffsetY,
                    originX: 'center',
                    originY: 'center',
                    width: getTextSize(currentText, canvas).width,
                    fontSize: canvas.fontSize,
                    textAlign: 'center',
                    type: 'textbox',
                    lockScalingX: true,
                    lockScalingY: true,
                    __skipDimension: true
                });

                newText.setControlsVisibility({
                    mt: false, // middle top
                    mb: false, // midle bottom
                    ml: false, // middle left
                    mr: false, // middle right
                    bl: false, // bottom left
                    br: false, // bottom right
                    tl: false, // top left
                    tr: false, // top right
                    mtr: false // middle-top-rotate control
                });

                if (_fontFamily) {
                    newText.fontFamily = _fontFamily;
                }

                if (newText.left < iconWidthFromCenter) {
                    newText.left = iconWidthFromCenter + offset;
                } else if (newText.left + iconWidthFromCenter > canvas.width) {
                    newText.left = canvas.width - iconWidthFromCenter - offset;
                }

                if (newText.top < iconHeightFromCenter) {
                    newText.top = iconHeightFromCenter + offset;
                } else if (newText.top + iconHeightFromCenter > canvas.height) {
                    newText.top = canvas.height - iconHeightFromCenter - offset;
                }

                canvas.add(newText);
                currentObject = newText;

                return false;
            }

            if (loadingImage === true) {
                return;
            }

            // Image content
            let img: any = document.querySelector('img.img_dragging');
            if (img) {
                dropCallback();

                // Comment textbox starts here
                if (img.classList.contains(Strings.commentClassName)) {
                    let placeHolder = Translate(
                        'plans/annotation/toolbar/commenting/text-area-placeholder'
                    );

                    let textbox = new fabric.SuperTextbox(placeHolder, {
                        left: pointer.x - mouseOffsetX,
                        top: pointer.y - mouseOffsetY,
                        width: 100,
                        fontSize: canvas.fontSize,
                        fontFamily: 'Trebuchet MS'
                    });
                    currentObject = textbox;
                    canvas.add(textbox);
                    canvas.renderAll();

                    dropCommentCallback();
                } else {
                    loadingImage = true;
                    fabric.Image.fromURL(img.currentSrc ? img.currentSrc : img.src, (image) => {
                        image.set({
                            left: pointer.x - mouseOffsetX,
                            top: pointer.y - mouseOffsetY
                        });

                        canvas.add(image);
                        currentObject = image;
                        loadingImage = false;
                    });
                }
                return false;
            }
        } else {
            currentObject.set({
                left: pointer.x - mouseOffsetX,
                top: pointer.y - mouseOffsetY
            });
            currentObject.setCoords();
            setControlBorderSettings(currentObject);
            canvas.renderAll();
        }
        return false;
    }

    function dragLeave(event: any) {
        if (currentObject) {
            canvas.remove(currentObject);
            currentObject = null;
        }
    }

    function drop(event: any) {
        event.preventDefault();
        let images = document.querySelectorAll('img.annotation-symbol');
        forEachNode(images, (img) => {
            img.classList.remove('img_dragging');
        });

        if (event.stopPropagation) {
            event.stopPropagation();
        }

        if (currentObject) {
            if (currentObject.get('type') === 'textbox') {
                currentObject.selectionStart = 0;
                currentObject.selectionEnd = currentObject.text.length;
                currentObject.enterEditing();

                canvas.setActiveObject(currentObject);

                addMovementAxisLimitation(currentObject, onlyTexts, canvas);
                addRemoveEmptyTextBoxesEvent(
                    currentObject,
                    onlyTexts,
                    canvas,
                    exitTextEditingCallback
                );
                currentObject.hoverCursor = 'default';
                currentObject.moveCursor = 'default';
                currentObject.defaultCursor = 'default';
            }

            currentObject = null;
            currentText = null;
        }

        canvasStates.push(canvas.toJSON(whitelistProperties));
    }
}

function addMovementAxisLimitation(text: any, onlyTexts: boolean, canvas: any): void {
    if (onlyTexts) {
        function moving(event: any) {
            let obj = canvas.getActiveObject();
            if (obj.lockMovementY || obj.lockMovementX) {
                return;
            }

            let horizontalMovement = Math.abs(event.e.movementX);
            let verticalMovement = Math.abs(event.e.movementY);

            if (event.e.shiftKey && horizontalMovement > verticalMovement) {
                obj.lockMovementY = true;
            } else if (event.e.shiftKey && verticalMovement > horizontalMovement) {
                obj.lockMovementX = true;
            }
        }

        function moved(event: any) {
            canvas.getActiveObject().lockMovementY = false;
            canvas.getActiveObject().lockMovementX = false;
        }

        text.on('moving', moving);
        text.on('mouseup', moved);
    }
}

function addRemoveEmptyTextBoxesEvent(
    currentObject: any,
    onlyTexts: boolean,
    canvas: any,
    callback: (isEditingText: boolean) => void
): void {
    if (!onlyTexts) {
        return;
    }

    currentObject.on('editing:exited', () => {
        let emptyTextBoxes = canvas.getObjects().filter((x) => x.type === 'textbox' && !x.text);
        for (let i = 0; i < emptyTextBoxes.length; i++) {
            canvas.remove(emptyTextBoxes[i]);
        }

        callback(false);
    });
}

export function duplicateObjects(
    fabric: any,
    canvas: any,
    callback: (isEditingText: boolean) => void,
    textsOnly?: boolean
) {
    let activeObject = canvas.getActiveObject();

    if (activeObject) {
        activeObject.clone((clonedObject) => {
            let oldLeft = clonedObject.left;
            let oldTop = clonedObject.top;

            if (clonedObject.type !== 'textbox' || clonedObject.isComment) {
                clonedObject.set({
                    left: oldLeft + 10,
                    top: oldTop + 10
                });
                addRemoveEmptyTextBoxesEvent(clonedObject, textsOnly, canvas, callback);
            }

            let boundingRect = clonedObject.getBoundingRect(true);
            if (boundingRect.left + boundingRect.width > canvas.getWidth()) {
                clonedObject.set({
                    left: oldLeft
                });
            }
            if (boundingRect.top + boundingRect.height > canvas.getHeight()) {
                clonedObject.set({
                    top: oldTop
                });
            }

            if (clonedObject.type === 'textbox' && !clonedObject.isComment) {
                clonedObject.setControlsVisibility({
                    mt: false, // middle top
                    mb: false, // midle bottom
                    ml: false, // middle left
                    mr: false, // middle right
                    bl: false, // bottom left
                    br: false, // bottom right
                    tl: false, // top left
                    tr: false, // top right
                    mtr: false // middle-top-rotate control
                });
            }

            if (clonedObject.type === 'activeSelection') {
                clonedObject.forEachObject((obj) => {
                    canvas.add(obj);
                });
            } else {
                canvas.add(clonedObject);
            }
            canvas.setActiveObject(clonedObject);
            canvas.renderAll();
            canvasStates.push(canvas.toJSON(whitelistProperties));
        }, customPropertiesWhitelistForCloning);
    }
}

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see http://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
function getTextSize(text, canvas) {
    let context = canvas.getContext('2d');

    context.font = context.font = `${canvas.fontSize}px ${canvas.fontFamily}`;

    let spaceWidth = context.measureText(' ').width;
    let newLines = text.split('\n');
    let width = 0;

    for (let i = 0; i < newLines.length; i++) {
        const line = newLines[i];

        let texts = line.split(' ');
        let numberOfSpaces = texts.length - 1;

        let lineWidth = 0;
        for (let i = 0; i < texts.length; i++) {
            const textInCorrectForm = _drawTextsAsUpperCase ? texts[i].toUpperCase() : texts[i];
            let textWidth = context.measureText(textInCorrectForm).width;
            lineWidth += textWidth;
        }

        lineWidth = lineWidth + spaceWidth * numberOfSpaces;

        if (newLines.length > 1) {
            width = Math.max(width, lineWidth);
        } else {
            width = lineWidth;
        }
    }

    return {
        width: width + 15,
        height: parseInt(context.font)
    };
}

function calcMouseOffset(newValue, oldValue, oldOffset) {
    return (newValue / oldValue) * oldOffset;
}

export function flipHorizontal(fabric: any, canvas: any): void {
    let group = selectAll(fabric, canvas);

    group.flipX = !group.flipX;

    canvas.getObjects().forEach((x) => {
        if (x.text) {
            x.flipX = !x.flipX;
        }
    });

    canvas.discardActiveObject();
    canvas.renderAll();
}

export function flipVertical(fabric, canvas: any): void {
    let group = selectAll(fabric, canvas);

    group.flipY = !group.flipY;

    canvas.getObjects().forEach((x) => {
        if (x.text) {
            x.flipY = !x.flipY;
        }
    });

    canvas.discardActiveObject();
    canvas.renderAll();
}

export function toggleDrawing(canvas: any, drawEnabled: boolean): void {
    canvas.isDrawingMode = drawEnabled;
}

let cropDataUrl = null;
export function crop(fabric: any, canvas: any, containerWidth: number): void {
    canvas.setZoom(1);
    let objects = canvas.getObjects();
    let originalImage = objects[0];

    let rect = objects.find((x) => x.id === 'cropRect');

    if (!rect) {
        return;
    }

    let rectBoundingRect = rect.getBoundingRect();

    let rectangle = {
        left: rectBoundingRect.left,
        top: rectBoundingRect.top,
        width: rectBoundingRect.width,
        height: rectBoundingRect.height
    };

    canvas.remove(rect);

    let canvasObjects = fabric.util.object.clone(objects.filter((x) => x !== originalImage));
    canvasObjects = Object.keys(canvasObjects).map((i) => canvasObjects[i]);

    const rectLeft = rectangle.left;
    const rectRight = rectLeft + rectangle.width;
    const rectTop = rectangle.top;
    const rectBottom = rectangle.top + rectangle.height;

    canvasObjects = canvasObjects.filter((item) => {
        const itemBoundRect = item.getBoundingRect();
        const itemLeft = itemBoundRect.left;
        const itemRight = itemLeft + itemBoundRect.width;
        const itemTop = itemBoundRect.top;
        const itemBottom = itemTop + itemBoundRect.height;

        if (
            itemLeft < rectRight &&
            itemRight > rectLeft &&
            itemTop < rectBottom &&
            itemBottom > rectTop
        ) {
            return true;
        } else {
            return false;
        }
    });

    canvasObjects.forEach((item) => {
        item.set({
            left: item.left - rectangle.left,
            top: item.top - rectangle.top
        });
    });

    let cropped = new Image();
    cropped.src = originalImage.toDataURL(Object.assign(rectangle, { format: 'jpeg' }));

    cropped.onload = function () {
        let image = new fabric.Image(cropped);
        image.left = 0;
        image.top = 0;
        image.selectable = false;
        image.originX = 'left';
        image.originY = 'top';
        image.backgroundColor = 'rgb(255,255,255)';
        image.setCoords();
        setControlBorderSettings(image);
        canvas.clear();
        cropDataUrl = image.toDataURL({ format: 'jpeg' });
        canvas.add(image);

        canvasObjects.forEach((x) => {
            if (x.type !== 'textbox' || x.isComment) {
                x.selectable = true;
            }

            canvas.add(x);
        });

        canvas.renderAll();
        rev = true;
        canvasResize(fabric, canvas, containerWidth);
    };
}

export function getCropDataUrl() {
    return cropDataUrl;
}

export function cropMutiple(fabric: any, canvas: any, canvasContainer: any): Promise<any[]> {
    canvas.setZoom(1);
    const objects = canvas.getObjects();
    const originalImage = objects[0];
    let newImages = [];

    let rectangles = objects
        .filter((x) => x.id === 'cropRect')
        .map((x) => {
            const rectBoundingRect = x.getBoundingRect();
            return {
                left: rectBoundingRect.left,
                top: rectBoundingRect.top,
                width: rectBoundingRect.width,
                height: rectBoundingRect.height
            };
        });

    let canvasObjects = fabric.util.object.clone(
        objects.filter((x) => x !== originalImage && x.id !== 'cropRect')
    );
    canvasObjects = Object.keys(canvasObjects).map((i) => canvasObjects[i]);

    return new Promise((resolve, reject) => {
        for (let i = 0; i < rectangles.length; i++) {
            let rectCanvasObjects = canvasObjects.filter((item) => {
                let boundRect = item.getBoundingRect();
                let left = boundRect.left;
                let top = boundRect.top;
                if (
                    (left >= rectangles[i].left ||
                        left <= rectangles[i].left + rectangles[i].width) &&
                    (top >= rectangles[i].top || top <= rectangles[i].top + rectangles[i].height)
                ) {
                    return true;
                } else {
                    return false;
                }
            });

            let items = rectCanvasObjects.map((item, index: number) => {
                let localItem = fabric.util.object.clone(item);

                localItem.set({
                    left: localItem.left - rectangles[i].left,
                    top: localItem.top - rectangles[i].top
                });
                localItem.selectable = true;
                return localItem;
            });

            let cropped = new Image();
            cropped.src = originalImage.toDataURL(Object.assign(rectangles[i], { format: 'jpeg' }));

            cropped.onload = function () {
                let image = new fabric.Image(cropped);
                image.left = 0;
                image.top = 0;
                image.selectable = false;
                image.originX = 'left';
                image.originY = 'top';
                image.setCoords();
                setControlBorderSettings(image);
                newImages.push({ image: image, objects: items });

                if (rectangles.length === newImages.length) {
                    resolve(newImages);
                }
            };
        }
    });
}

function zoomAndPanAfterCrop(image: any, cropRectangle: any) {
    let xScale = image.width / cropRectangle.width;
    let yScale = image.height / cropRectangle.height;
    let left = cropRectangle.left * -1;
    let top = cropRectangle.top * -1;
    image.set({
        left: left,
        top: top,
        originX: 'left',
        originY: 'top',
        scaleX: xScale,
        scaleY: yScale
    });
}

export function trimAndResizeTexts(canvas) {
    const objects = canvas.getObjects();

    for (let i = 0; i < objects.length; i++) {
        const item = objects[i];

        if (item.type === 'textbox' && !item.isComment) {
            trimAndResizeText(canvas, item);
        }
    }

    canvas.renderAll();
}

function trimAndResizeText(canvas, item) {
    item.text = item.text.trim();
    item.text = _drawTextsAsUpperCase ? item.text.toUpperCase() : item.text;

    item.set({
        width: getTextSize(item.text, canvas).width
    });
}

export function getSaveData(fabric: any, canvas: any): string {
    let json = canvas.toJSON(whitelistProperties);

    json.objects.forEach((object) => {
        if (object.src) {
            object.src = makeAbsoluteUrlRelative(object.src);
        }
    });

    json.objects[0].src = '';
    json.objects[0].backgroundColor = 'rgb(255,255,255)';

    return JSON.stringify({ objects: json.objects });
}

export function copyRectangleTransform(fabric: any, rectangle: any): any {
    return new fabric.Rect({
        width: rectangle.width,
        height: rectangle.height,
        left: rectangle.left,
        top: rectangle.top
    });
}

export function areAnyObjectsPlaced(canvas: any) {
    if (canvas._objects.length === 1) {
        return false;
    }

    let result = false;
    for (let o of canvas._objects) {
        if (o.type === 'image' && o !== canvas._objects[0]) {
            return true;
        } else if (o.type === 'path') {
            return true;
        } else if (o.type === 'rect') {
            if (!o.name || o.name !== Strings.cropRectangleName) {
                return true;
            }
        } else if (o.type === 'textbox') {
            return true;
        }
    }

    return result;
}

export function fontSizeChange(
    canvas: any,
    annotationType: string,
    disableAutoWidthForComments = false
) {
    if (canvas._objects.length === 0) {
        return;
    }

    for (let o of canvas._objects) {
        if (
            (annotationType === AnnotationTypes.AnnotationNormal ||
                annotationType === AnnotationTypes.ExternalAnnotation ||
                annotationType === AnnotationTypes.DeliveryAnnotation ||
                annotationType === AnnotationTypes.ExternalDeliveryAnnotation) &&
            !o.isComment
        ) {
            continue;
        }

        if (o.fontSize) {
            let width =
                o.isComment && disableAutoWidthForComments
                    ? o.width
                    : getTextSize(o.text, canvas).width;
            o.set({
                fontSize: canvas.fontSize,
                originX: 'center',
                originY: 'center',
                width: width
            });
        }
    }

    canvas.renderAll();
}

export function strokeWidthChange(width: number) {
    _strokeWidth = width;
}

export function strokeColorChange(color: any) {
    _strokeColor = color;
}

export function deleteObjects(canvas) {
    let group = canvas.getActiveObjects();
    if (group) {
        for (let object of group) {
            canvas.remove(object);
        }

        canvas.discardActiveObject();
        canvas.renderAll();
    }
}

export function deleteAllObjectsExceptsTexts(canvas) {
    let objects = canvas.getObjects().filter((x) => x.type !== 'textbox');
    if (objects) {
        for (let i = 1; i < objects.length; i++) {
            canvas.remove(objects[i]);
        }
    }

    canvas.renderAll();
}

export function reset(
    fabric: any,
    canvas: any,
    state: any,
    url: string,
    annotationType: string,
    fontSize: number | null
) {
    // Canvas original size
    canvas.setWidth(300);
    canvas.setHeight(150);
    canvas.absolutePan(new fabric.Point(0, 0));
    canvas.setZoom(1);

    toggleDrawing(canvas, false);
    addObjectsToCanvas(fabric, canvas, state, url, annotationType, false, fontSize);
}

export function selectAll(fabric: any, canvas: any) {
    let objs = canvas.getObjects().map((o) => {
        o = o.set({ active: false, hasBorder: false });

        return o;
    });

    let group = new fabric.ActiveSelection(objs, {
        originX: 'center',
        originY: 'center',
        canvas: canvas
    });

    canvas._activeObject = null;

    group.setCoords();
    setControlBorderSettings(group);
    canvas.setActiveObject(group);
    canvas.renderAll();

    return group;
}

export function rotate(fabric: any, canvas: any, containerWidth: any, angle: number) {
    canvas.setZoom(1);
    let group = selectAll(fabric, canvas);
    group.canvas = canvas;
    group.rotate(angle);
    canvas.renderAll();
    canvas.discardActiveObject();
    const objects = canvas.getObjects();
    objects.forEach((x) => {
        if (x.get('type') === 'textbox') {
            x.angle = 0;
            x.setCoords();
            setControlBorderSettings(x);

            canvas.renderAll();
        }
    });
    canvasResize(fabric, canvas, containerWidth);
}

export function toggleRotateFreely(
    toggle: boolean,
    fabric: any,
    canvas: any,
    containerWidth: any,
    step?: number
) {
    if (toggle) {
        let group = getRotateGroup(fabric, canvas);
        let diagonal = Math.sqrt(Math.pow(group.width, 2) + Math.pow(group.height, 2));

        group.left = diagonal / 2;
        group.top = diagonal / 2 + 100;

        zoomToFitImage(canvas, diagonal, diagonal);

        group.canvas = canvas;
        canvas.renderAll();

        canvas.on('selection:cleared', function (event) {
            canvas.setActiveObject(getRotateGroup(fabric, canvas));
        });

        canvas.on('object:rotating', (event) => {
            if (event.e.ctrlKey || step) {
                let stepValue = step ? step : 90;
                event.target.angle = Math.round(event.target.angle / stepValue) * stepValue;
            }
        });

        setSelectedCornerAction(fabric, 'rotate', 'crosshair');
        canvas.calcOffset();
    } else {
        canvas.off('selection:cleared');
        canvas.setZoom(1);
        resetGridImage();

        let group = selectAll(fabric, canvas);
        let boundRect = group.getBoundingRect();

        group.left = boundRect.width / 2;
        group.top = boundRect.height / 2;

        canvas.discardActiveObject();

        canvas.absolutePan(new fabric.Point(0, 0));

        zoomToFitImage(canvas, boundRect.width, boundRect.height);

        const objects = canvas.getObjects();
        objects.forEach((x) => {
            if (x.get('type') === 'textbox') {
                x.angle = 0;
                x.setCoords();
                setControlBorderSettings(x);
                canvas.renderAll();
            }
        });

        setSelectedCornerAction(fabric);
        canvas.backgroundColor = 'rgb(255,255,255)';
    }
}

export function setGridImage() {
    let imageEl = document.getElementById('grid2400');
    imageEl.style.display = 'block';
}

export function resetGridImage() {
    let imageEl = document.getElementById('grid2400');
    imageEl.style.display = 'none';
}

export function canvasResize(fabric: any, canvas: any, containerWidth: number): boolean {
    let objects = canvas.getObjects();

    if (objects.length === 0) {
        return false;
    }

    let image = objects[0];
    let rect = new fabric.Rect({
        width: image._originalElement.width,
        height: image._originalElement.height,
        angle: image.angle
    });
    let rectBoudingRect = rect.getBoundingRect();
    let originalWidth = rectBoudingRect.width;
    let originalHeight = rectBoudingRect.height;

    if (containerWidth > originalWidth) {
        containerWidth = originalWidth;
    }

    let newHeight = (originalHeight / originalWidth) * containerWidth;

    let roundNumber = 10;
    let factorX =
        Math.round((containerWidth / (rev ? originalWidth : canvas.getWidth())) * roundNumber) /
        roundNumber;
    let factorY =
        Math.round((newHeight / (rev ? originalHeight : canvas.getHeight())) * roundNumber) /
        roundNumber;

    canvas.setWidth((rev ? originalWidth : canvas.getWidth()) * factorX);
    canvas.setHeight((rev ? originalHeight : canvas.getHeight()) * factorY);

    let boundRect = objects[0].getBoundingRect();
    canvas.setZoom(1);
    zoomToFitImage(canvas, boundRect.width, boundRect.height);

    if (rev) {
        rev = false;
    }

    canvas.renderAll();
    canvas.calcOffset();
    setGroupPosition(fabric, canvas, 0, 0);

    let originalImage = canvas.getObjects()[0];
    let originalImageBoundingRect = originalImage.getBoundingRect();

    setGroupPosition(
        fabric,
        canvas,
        -originalImageBoundingRect.left,
        -originalImageBoundingRect.top
    );

    zoomAndPan(fabric, canvas);

    return true;
}

function zoomToFitImage(canvas: any, width: number, height: number) {
    let zoom = 1;
    if (width < canvas.getWidth()) {
        zoom = canvas.getHeight() / height;
        if (zoom * width > canvas.getWidth()) {
            zoom = canvas.getWidth() / width;
        }
    } else {
        zoom = canvas.getWidth() / width;
        if (zoom * height > canvas.getHeight()) {
            zoom = canvas.getHeight() / height;
        }
    }
    canvas.setZoom(zoom);
}

function zoomAndPan(fabric: any, canvas: any) {
    canvas.setZoom(1);
    canvas.absolutePan(new fabric.Point(0, 0));
    const boundRect = canvas.getObjects()[0].getBoundingRect();
    zoomToFitImage(canvas, boundRect.width, boundRect.height);
}

function getRotateGroup(fabric: any, canvas: any) {
    let group = selectAll(fabric, canvas);

    group.selectable = true;
    group.lockMovementX = true;
    group.lockMovementY = true;

    group.setControlsVisibility({
        mt: true, // middle top
        mb: true, // midle bottom
        ml: true, // middle left
        mr: true, // middle right
        bl: true, // bottom left
        br: true, // bottom right
        tl: true, // top left
        tr: true // top right
    });

    return group;
}

function makeAbsoluteUrlRelative(url: string) {
    if (url) {
        // not absolute -> short circuit
        if (url.startsWith('/') || url.startsWith('data')) {
            return url;
        }
        return url.replace(/^.+?[^\/:](?=[?\/]|$)/, '');
    }
    return url;
}

export function dataURLtoBlob(dataurl): Blob {
    let arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
}

export function toggleCrop(toggle: boolean, fabric: any, canvas: any) {
    if (toggle) {
        let group = selectAll(fabric, canvas);
        group.selectable = false;

        const image = canvas.getObjects()[0];
        let boundRect = image.getBoundingRect();
        zoomToFitImage(canvas, boundRect.width * 2, boundRect.height * 2);

        group.canvas = canvas;
        canvas.discardActiveObject();
        canvas.renderAll();
        canvas.calcOffset();
    } else {
        canvas.setZoom(1);
        const image = canvas.getObjects()[0];
        let boundRect = image.getBoundingRect();
        canvas.absolutePan(new fabric.Point(0, 0));
        zoomToFitImage(canvas, boundRect.width, boundRect.height);
        canvas.discardActiveObject();
    }
}

export function onSpaceKey(canvas: any) {
    let activeObject = canvas.getActiveObject();

    if (activeObject.type === 'textbox' && activeObject.isComment) {
        activeObject.toggleCorner();
        canvas.renderAll();
    }
}

export function undo(canvas: any, fabric: any) {
    let o;
    if (canvasStates.length > 1) {
        canvasStates.pop();
        o = canvasStates[canvasStates.length - 1];
    } else if (canvasStates.length > 0 && defaultCanvasState) {
        canvasStates.pop();
        o = defaultCanvasState;
    } else {
        return;
    }

    o.objects[0].selectable = false;
    canAddToStateArray = false;
    canvas.loadFromJSON(JSON.stringify(o), () => {
        canvas.renderAll.bind(canvas);
        createSuperTextboxes(canvas, fabric);
        canAddToStateArray = true;
    });
}

function setSelectedCornerAction(fabric: any, newAction?: string, newCursor?: string) {
    fabric.Canvas.prototype.customiseControls({
        mt: {
            action: newAction ? newAction : 'scaleY',
            cursor: newCursor ? newCursor : 'n-resize'
        },
        mr: {
            action: newAction ? newAction : 'scaleX',
            cursor: newCursor ? newCursor : 'e-resize'
        },
        mb: {
            action: newAction ? newAction : 'scaleY',
            cursor: newCursor ? newCursor : 's-resize'
        },
        ml: {
            action: newAction ? newAction : 'scaleX',
            cursor: newCursor ? newCursor : 'w-resize'
        },
        tl: {
            action: newAction ? newAction : 'scale',
            cursor: newCursor ? newCursor : 'nw-resize'
        },
        tr: {
            action: newAction ? newAction : 'scale',
            cursor: newCursor ? newCursor : 'ne-resize'
        },
        br: {
            action: newAction ? newAction : 'scale',
            cursor: newCursor ? newCursor : 'se-resize'
        },
        bl: {
            action: newAction ? newAction : 'scale',
            cursor: newCursor ? newCursor : 'sw-resize'
        }
    });
}

export function restoreZoom(canvas: any) {
    canvas.setZoom(1);
}

export function restoreCanvasPosition(canvas: any) {
    canvas.setZoom(1);
    const image = canvas.getObjects()[0];
    let boundRect = image.getBoundingRect();
    zoomToFitImage(canvas, boundRect.width, boundRect.height);
    canvas.discardActiveObject();
    canvas.viewportTransform[4] = 0;
    canvas.viewportTransform[5] = 0;
}

function createSuperTextboxes(canvas: any, fabric: any) {
    let textboxes = canvas.getObjects().filter((x) => x.type === 'textbox' && x.isComment);
    for (let i = 0; i < textboxes.length; i++) {
        const item = textboxes[i];
        let textbox = new fabric.SuperTextbox(item.text, {
            left: item.left,
            top: item.top,
            width: item.width,
            height: item.height,
            fontSize: item.fontSize,
            fontFamily: item.fontFamily,
            textLines: item.textLines,
            originX: item.originX,
            originY: item.originY,
            scaleX: item.scaleX,
            scaleY: item.scaleY,
            corner: item.corner
        });
        canvas.remove(item);
        canvas.add(textbox);
    }
}

export function zoom(zoomAmount: number, canvas: any) {
    let zoom = canvas.getZoom();
    zoom = zoom + zoomAmount / 2000;
    if (zoom > 20) zoom = 20;
    if (zoom < 0.1) zoom = 0.1;
    canvas.setZoom(zoom);
}
